import { useEffect, useState } from "react";
import { Plus, Loader2, Navigation } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { api, formatErr, mapsLink } from "@/lib/api";

const EMPTY = { hospital_name: "", doctor_contact_person: "", phone_number: "", address: "", city: "Varanasi", lat: "", lng: "" };

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const load = () => api.get("/clients").then((r) => setClients(r.data));
  useEffect(() => {
    load();
  }, []);

  const save = async (ev) => {
    ev.preventDefault();
    setBusy(true);
    try {
      await api.post("/clients", { ...f, lat: f.lat === "" ? null : Number(f.lat), lng: f.lng === "" ? null : Number(f.lng) });
      toast.success("Client added");
      setF(EMPTY);
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatErr(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <h1 className="font-heading text-4xl font-extrabold text-slate-900 tracking-tight">Clients / Hospitals</h1>
        <Button onClick={() => setOpen(true)} className="bg-sky-600 hover:bg-sky-700" data-testid="add-client-button"><Plus className="h-4 w-4 mr-2" />Add Client</Button>
      </div>
      <div className="bg-white border rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader><TableRow className="bg-slate-50"><TableHead>Hospital</TableHead><TableHead>Contact</TableHead><TableHead>Address</TableHead><TableHead /></TableRow></TableHeader>
          <TableBody>
            {clients.map((c) => (
              <TableRow key={c.id} data-testid={`client-row-${c.id}`}>
                <TableCell className="font-semibold">{c.hospital_name}</TableCell>
                <TableCell className="text-sm">{c.doctor_contact_person || "—"}<div className="text-xs text-slate-500">{c.phone_number}</div></TableCell>
                <TableCell className="text-sm">{c.address}, {c.city}</TableCell>
                <TableCell><a href={mapsLink(c)} target="_blank" rel="noreferrer" className="text-sky-700 text-sm inline-flex items-center gap-1"><Navigation className="h-3 w-3" />Map</a></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg" data-testid="client-dialog">
          <DialogHeader><DialogTitle className="font-heading text-xl">New Client</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label>Hospital / Clinic Name</Label><Input required value={f.hospital_name} onChange={set("hospital_name")} data-testid="client-name-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Doctor / Contact</Label><Input value={f.doctor_contact_person} onChange={set("doctor_contact_person")} data-testid="client-contact-input" /></div>
              <div><Label>Phone</Label><Input value={f.phone_number} onChange={set("phone_number")} data-testid="client-phone-input" /></div>
            </div>
            <div><Label>Address</Label><Input required value={f.address} onChange={set("address")} data-testid="client-address-input" /></div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>City</Label><Input value={f.city} onChange={set("city")} data-testid="client-city-input" /></div>
              <div><Label>Latitude</Label><Input type="number" step="any" value={f.lat} onChange={set("lat")} data-testid="client-lat-input" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={f.lng} onChange={set("lng")} data-testid="client-lng-input" /></div>
            </div>
            <Button type="submit" disabled={busy} className="w-full bg-sky-600 hover:bg-sky-700" data-testid="client-save-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Client"}</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
